import { useCallback, useState } from "react";
import { Button, FileUpload, Flex, ModalV2, Text, type FileObjectType } from "@jsq/ds/core";
import { Form, FormNumberInput, FormSelect, FormTextarea } from "@jsq/ds/forms";
import { Colors } from "@jsq/ds/styles";
import { Money, MoneyFormat } from "~/components/Money";
import { SPACING } from "~/styles/variables";
import {
  EDIT_EUM_CALCULATION_VALUES_LABEL,
  REASON_CODE_OPTIONS,
  SUPPORTED_FILE_TYPES,
} from "../constants";
import { usdDollarsToMoney } from "../utils/moneyUtils";
import { divider, scrollableBody } from "./styles_vanilla.css";
import type {
  EditEumCalculationValuesFormValues,
  EditEumCalculationValuesModalProps,
  ValidatedEditEumCalculationValuesFormValues,
} from "./types";

const INITIAL_VALUES: EditEumCalculationValuesFormValues = {
  newNetCapitalValue: null,
  newTotalCommitmentValue: null,
  newDistributionsValue: null,
  reasonCode: null,
  description: "",
};

const isNumericValue = (value: number | null): value is number => typeof value === "number";

const isFormValid = (
  values: EditEumCalculationValuesFormValues,
): values is ValidatedEditEumCalculationValuesFormValues => {
  const hasReason = typeof values.reasonCode === "string" && values.reasonCode.length > 0;
  const hasAtLeastOneNewValue =
    isNumericValue(values.newNetCapitalValue) ||
    isNumericValue(values.newTotalCommitmentValue) ||
    isNumericValue(values.newDistributionsValue);
  return hasReason && hasAtLeastOneNewValue;
};

type CurrentValueRowProps = {
  label: string;
  amount: number | null;
  testId: string;
};

const CurrentValueRow = ({ label, amount, testId }: CurrentValueRowProps) => (
  <Flex flexDirection="column" gap={SPACING.X_SMALL}>
    <Text variant="secondary-sm" color={Colors.Neutral600}>
      {label}
    </Text>
    <Text variant="strong-md" color={Colors.Neutral900} data-testid={testId}>
      <Money
        money={usdDollarsToMoney(amount)}
        format={MoneyFormat.Symbol}
        rounded
        dashesAsDefault
      />
    </Text>
  </Flex>
);

type EditableValueFieldProps = {
  name: string;
  label: string;
};

const EditableValueField = ({ name, label }: EditableValueFieldProps) => (
  <Flex flexDirection="column" gap={SPACING.X_SMALL}>
    <Text variant="regular-md" color={Colors.Neutral900}>
      {label}
    </Text>
    <FormNumberInput
      name={name}
      placeholder="0.00"
      preAdornment="$"
      min={0}
      hideSteps
      isFullWidth
      aria-label={label}
    />
  </Flex>
);

export const EditEumCalculationValuesModal = ({
  isOpen,
  onClose,
  onSubmit,
  selectedEntityCount,
  currentValues,
}: EditEumCalculationValuesModalProps) => {
  const [file, setFile] = useState<File | null>(null);

  const handleFileSelected = (selectedFiles: FileObjectType[]) => {
    const accepted = selectedFiles.find((f) => f.status === "accepted");
    setFile(accepted ? accepted.file : null);
  };

  const handleFileDelete = () => {
    setFile(null);
  };

  const handleClose = useCallback(() => {
    setFile(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(
    async (values: EditEumCalculationValuesFormValues) => {
      if (isFormValid(values)) {
        await onSubmit({
          newNetCapitalValue: values.newNetCapitalValue,
          newTotalCommitmentValue: values.newTotalCommitmentValue,
          newDistributionsValue: values.newDistributionsValue,
          reasonCode: values.reasonCode,
          description: values.description.trim(),
          file,
        });
        setFile(null);
      }
    },
    [onSubmit, file],
  );

  if (!isOpen) {
    return null;
  }

  const entityLabel = selectedEntityCount === 1 ? "entity" : "entities";

  return (
    <ModalV2
      show={isOpen}
      onHide={handleClose}
      title={EDIT_EUM_CALCULATION_VALUES_LABEL}
      description={`${selectedEntityCount} selected ${entityLabel}`}
      data-testid="edit-eum-calculation-values-modal"
    >
      <Form<EditEumCalculationValuesFormValues>
        initialValues={INITIAL_VALUES}
        onSubmit={handleSubmit}
        submitOnlyChangedValues={false}
      >
        <ModalV2.Body className={scrollableBody}>
          <Flex flexDirection="column" gap={SPACING.LARGE}>
            <CurrentValueRow
              label="Current EUM value"
              amount={currentValues.eumValue}
              testId="current-eum-value"
            />
            <div className={divider} />
            <CurrentValueRow
              label="Current Net capital value"
              amount={currentValues.netCapitalValue}
              testId="current-net-capital-value"
            />
            <EditableValueField name="newNetCapitalValue" label="New Net capital value" />
            <CurrentValueRow
              label="Current Total commitment value"
              amount={currentValues.totalCommitmentValue}
              testId="current-total-commitment-value"
            />
            <EditableValueField
              name="newTotalCommitmentValue"
              label="New Total commitment value"
            />
            <CurrentValueRow
              label="Current Distributions value"
              amount={currentValues.distributionsValue}
              testId="current-distributions-value"
            />
            <EditableValueField name="newDistributionsValue" label="New Distributions value" />
            <FormSelect
              name="reasonCode"
              label="Reason code"
              placeholder="Select option"
              data-testid="reason-code-select"
              full
            >
              <FormSelect.Section>
                {REASON_CODE_OPTIONS.map((option) => (
                  <FormSelect.Option key={option.value} value={option.value}>
                    {option.label}
                  </FormSelect.Option>
                ))}
              </FormSelect.Section>
            </FormSelect>
            <FormTextarea
              name="description"
              label="Description"
              placeholder="Add further details for this approval"
              size="small"
              isFullWidth
            />
            <Flex flexDirection="column" gap={SPACING.X_SMALL}>
              <Flex alignItems="center" gap={SPACING.X_SMALL}>
                <Text variant="strong-md" color={Colors.Neutral900}>
                  Supporting document
                </Text>
                <Text asSpan variant="regular-md" color={Colors.Neutral600}>
                  (optional)
                </Text>
              </Flex>
              <FileUpload
                isFullWidth
                size="small"
                canAllowMultiple={false}
                acceptedFileTypes={SUPPORTED_FILE_TYPES}
                onFileSelected={handleFileSelected}
                onDelete={handleFileDelete}
                data-testid="supporting-document-upload"
              />
            </Flex>
          </Flex>
        </ModalV2.Body>
        <ModalV2.Footer>
          <Flex justifyContent="flex-end" gap={SPACING.SMALL}>
            <Button variant="neutral" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Submit for approval
            </Button>
          </Flex>
        </ModalV2.Footer>
      </Form>
    </ModalV2>
  );
};
